import axios, { type AxiosInstance } from 'axios'

import { isNative } from './platform'
import { edition, isVppEdition, vppEditionKey } from './edition'
import { recordRetirementIfTransferred } from './transfer-retirement'
import {
  clearPatientToken,
  getPatientToken,
  getTherapistToken,
  setPatientToken,
} from './auth-token'

/**
 * Axios instance shared by the entire front-end.
 *
 * `baseURL` is driven by `VITE_API_BASE_URL`:
 *   - web build  → "/api" (same-origin, Laravel responds at the same host)
 *   - capacitor  → absolute URL to the deployed Laravel host (.env.capacitor)
 *
 * The request interceptor attaches a bearer token chosen by platform:
 *   - native → patient eternal token (stored in @capacitor/preferences)
 *   - web    → therapist JWT (stored in localStorage / dev env var)
 *
 * Neither flow uses cookies, so `withCredentials` stays false and we do
 * not need a CSRF cookie request.
 */
export const api: AxiosInstance = axios.create({
  // Web (therapist) is same-origin, so "/api". Native (Capacitor) overrides
  // this with an absolute URL via .env.capacitor. Fall back to "/api" when
  // VITE_API_BASE_URL is unset so a web env that forgot it still hits the API
  // instead of falling through to the SPA catch-all (which returns index.html).
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  withCredentials: false,
})

api.interceptors.request.use(async (config) => {
  const token = isNative ? await getPatientToken() : getTherapistToken()
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }
  return config
})

/** Transfer endpoints that must not trigger the retirement probe below. */
const TRANSFER_SELF_CALLS = ['/patient/transfer/tombstone', '/patient/transfer/claim']

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 401 on native means the (supposedly eternal) patient token was
    // revoked server-side. Clear it so the next launch re-bootstraps.
    if (isNative && error?.response?.status === 401) {
      // ...but first find out whether it was revoked because the account moved
      // to another device. Re-bootstrapping in that case would tell the user
      // nothing and hand out a fresh 30-day trial, so a transfer would double
      // as a trial faucet. See lib/transfer-retirement.ts.
      const url = typeof error?.config?.url === 'string' ? error.config.url : ''
      if (!TRANSFER_SELF_CALLS.some((path) => url.includes(path))) {
        const token = await getPatientToken()
        if (token) await recordRetirementIfTransferred(token)
      }
      await clearPatientToken()
    }
    return Promise.reject(error)
  },
)

/**
 * One-shot patient bootstrap: called on first native launch only. Creates
 * the opaque Patient on the server, stores the eternal token locally.
 *
 * Idempotency lives client-side: once a token exists we never call this
 * again, even if the app is reinstalled (token in keychain, persists).
 */
export async function ensurePatientToken(): Promise<string> {
  const existing = await getPatientToken()
  if (existing) return existing

  // Bootstrap deliberately bypasses the request interceptor's token logic
  // because we have nothing to send yet.
  const { data } = await axios.post<{ token: string; patient_id: string }>(
    `${import.meta.env.VITE_API_BASE_URL}/patient/bootstrap`,
    {
      // Tells the server which commercial edition this bundle is, so it can
      // stamp the channel and skip the trial clock for licensed devices.
      channel: edition,
      // VPP only. identifierForVendor on iOS; the server HMACs it on arrival
      // and stores only the hash. It exists to tell "the app was removed and
      // reinstalled on this same iPad" apart from "the license moved to a
      // different iPad" — the licence-rotation question. Best-effort: a
      // failure here must never block a first launch.
      device_id: isVppEdition ? await vppDeviceId() : undefined,
    },
    {
      headers: {
        Accept: 'application/json',
        ...(vppEditionKey ? { 'X-LingoTalk-Edition-Key': vppEditionKey } : {}),
      },
    },
  )
  await setPatientToken(data.token)
  return data.token
}

/**
 * Best-effort device identifier for the VPP edition. Resolves to null on any
 * failure — the install still bootstraps, it just can't contribute to the
 * reinstall-vs-moved-license distinction.
 *
 * Worth knowing: identifierForVendor resets once the last app from a vendor is
 * removed from a device, which on a single-app fleet iPad is exactly the
 * license-revocation case. So this sharpens the mixed cases and degrades
 * gracefully to "looks like a new device" otherwise.
 */
async function vppDeviceId(): Promise<string | undefined> {
  try {
    const { Device } = await import('@capacitor/device')
    const { identifier } = await Device.getId()
    return identifier || undefined
  } catch {
    return undefined
  }
}

// --- Monetization endpoints ----------------------------------------------

export type EntitlementStatus =
  | 'trial'
  | 'subscription'
  | 'lifetime'
  // Institution-purchased VPP license. Unlocked, never expires client-side:
  // access ends when the organization revokes the license and MDM removes the
  // app, which we deliberately do not try to detect (a blocking receipt check
  // would prompt for an Apple Account that managed iPads often don't have).
  | 'vpp'
  // A volume license redeemed from a facility's token, on the ordinary
  // consumer edition. Unlike 'vpp' this one CAN expire — the facility's
  // contract has an end date, or the license runs a fixed number of days from
  // activation — so the server reports `expires_at` and the client trusts it
  // exactly as it trusts a subscription's.
  | 'org'
  | 'lapsed'
  | 'none'

export type EntitlementResponse = {
  now: string
  trial: {
    started_at: string | null
    ends_at: string | null
    days_remaining: number | null
  }
  entitlement: {
    status: EntitlementStatus
    /** Which commercial edition the server has this install recorded as. */
    channel?: 'consumer' | 'vpp'
    expires_at: string | null
    product_id: string | null
    platform: 'ios' | 'android' | null
    /** The facility this device is licensed through, if any. Set whenever the
     *  device is linked, even once the license has lapsed — settings needs it
     *  to explain why access stopped. */
    organization_name?: string | null
    /** Non-null when a subscription past its stamped expiry is being honoured
     *  anyway: `window` if that expiry was recent, `unseen` if we had not seen
     *  this install since. Reported once per run; see
     *  EntitlementResolver::subscriptionGrace(). */
    grace?: 'window' | 'unseen' | null
  }
}

export type VerifyPurchaseRequest = {
  platform: 'ios' | 'android'
  product_id: string
  transaction_id: string
  jws_or_token: string | null
}

export type RestorePurchasesRequest = {
  platform: 'ios' | 'android'
  transactions: Array<{
    product_id: string
    transaction_id: string
    jws_or_token?: string | null
  }>
}

export async function fetchEntitlement(): Promise<EntitlementResponse> {
  const { data } = await api.get<EntitlementResponse>('/patient/entitlement')
  return data
}

export async function verifyPurchase(body: VerifyPurchaseRequest): Promise<EntitlementResponse> {
  const { data } = await api.post<EntitlementResponse>('/patient/purchase/verify', body)
  return data
}

export async function restorePurchases(body: RestorePurchasesRequest): Promise<EntitlementResponse> {
  const { data } = await api.post<EntitlementResponse>('/patient/purchase/restore', body)
  return data
}

/**
 * Exchange a facility's volume-license token for a license on this device.
 * Returns the full entitlement, so callers apply it straight into the store.
 *
 * Idempotent for a device already on that license — which is what lets the
 * launch-time MDM sync run without bookkeeping.
 */
export async function redeemOrgLicense(token: string): Promise<EntitlementResponse> {
  const { data } = await api.post<EntitlementResponse>('/patient/license/redeem', { token })
  return data
}

// --- Patient settings -----------------------------------------------------

export type ItemOrderOnRepeat = 'same' | 'randomized'
export type HelpPosition = 'left' | 'right'
export type SpeechRetries = 1 | 3

export type PatientSettings = {
  item_order_on_repeat: ItemOrderOnRepeat
  help_position: HelpPosition
  speech_retries: SpeechRetries
}

/**
 * Server-side defaults, mirrored from App\Services\PatientSettingsResolver::DEFAULTS.
 * Used as a seed while a fetch is in flight — by usePatientSettingsStore on the
 * patient device and by the therapist's launch-patient-settings screen.
 */
export const DEFAULT_PATIENT_SETTINGS: PatientSettings = {
  item_order_on_repeat: 'same',
  help_position: 'left',
  speech_retries: 3,
}

export async function fetchPatientSettings(): Promise<PatientSettings> {
  const { data } = await api.get<PatientSettings>('/patient/settings')
  return data
}

export async function updatePatientSettings(body: PatientSettings): Promise<PatientSettings> {
  const { data } = await api.put<PatientSettings>('/patient/settings', body)
  return data
}

// --- Patient support code -------------------------------------------------

export type SupportCode = {
  code: string
  /** ISO-8601 timestamp of the Monday (00:00 UTC) the code is valid from. */
  week_starts_at: string
}

export async function fetchSupportCode(): Promise<SupportCode> {
  const { data } = await api.get<SupportCode>('/patient/support-code')
  return data
}
